import {
  ICON,
  ICON_STATES,
  LABEL_FONT,
  LABEL_FONT_SETTINGS,
  PIN,
  PLACE_DOT,
  PLACE_FILL,
  PLACE_ICON,
  PLACE_LABEL,
  PLACE_LABEL_ZOOM,
} from './map-style';
import { LngLat, MapPlace } from './map-types';

/**
 * The selected suburb's places on the map (#253 step 3): in Conductor or on the website as the products' icons, the
 * rest as dots in their state's colour, the selected one ringed amber, and names at street zoom. Plus the pin of a
 * place being added by hand. A click on a marker goes to `pick` (it returns true when it took the click).
 */
export function placeLayers(
  deck: any,
  places: MapPlace[],
  selected: number | null,
  zoom: number,
  pick: (info: any) => boolean
): any[] {
  if (!places.length) return [];
  const dots = places.filter((p) => !ICON_STATES.has(p.state));
  const icons = places.filter((p) => ICON_STATES.has(p.state));
  const layers: any[] = [placeDots(deck, dots, places, selected, pick)];
  if (icons.length) layers.push(...placeIcons(deck, icons, selected, pick));
  if (zoom >= PLACE_LABEL_ZOOM) layers.push(placeLabels(deck, places));
  return layers;
}

const position = (p: MapPlace) => [p.lng, p.lat];

function placeDots(
  deck: any,
  dots: MapPlace[],
  all: MapPlace[],
  selected: number | null,
  pick: (info: any) => boolean
): any {
  const { radius, line, lineWidth } = PLACE_DOT;
  return new deck.ScatterplotLayer({
    id: 'places',
    data: dots,
    getPosition: position,
    getRadius: (p: MapPlace) =>
      (p.state === 'pending' ? radius.pending : p.state === 'unlinked' ? radius.unlinked : radius.other) +
      (p.id === selected ? radius.selectedExtra : 0),
    radiusUnits: 'pixels',
    getFillColor: (p: MapPlace) => [...PLACE_FILL[p.state], PLACE_DOT.alpha],
    getLineColor: (p: MapPlace) => dotLine(p, selected, line),
    getLineWidth: (p: MapPlace) => (p.id === selected ? lineWidth.selected : p.state === 'unlinked' ? lineWidth.unlinked : lineWidth.other),
    lineWidthUnits: 'pixels',
    stroked: true,
    pickable: true,
    updateTriggers: {
      getRadius: [selected],
      getLineColor: [selected],
      getLineWidth: [selected],
      getFillColor: [all],
    },
    onClick: pick,
  });
}

function dotLine(p: MapPlace, selected: number | null, line: typeof PLACE_DOT.line): number[] {
  if (p.id === selected) return line.selected;
  if (p.state === 'unlinked') return line.unlinked;
  return p.state === 'other' ? line.other : line.light;
}

/** Conductor's icon on a white disc, Picaluna's for the website, and Picaluna's with a small Conductor badge for both. */
function placeIcons(
  deck: any,
  icons: MapPlace[],
  selected: number | null,
  pick: (info: any) => boolean
): any[] {
  const both = icons.filter((p) => p.state === 'both');
  return [
    // The selected one gets an amber halo.
    new deck.ScatterplotLayer({
      id: 'place-halo',
      data: icons.filter((p) => p.id === selected),
      getPosition: position,
      getRadius: PLACE_ICON.haloRadius,
      radiusUnits: 'pixels',
      getFillColor: PLACE_ICON.halo,
      pickable: false,
    }),
    new deck.IconLayer({
      id: 'place-discs',
      data: icons.filter((p) => p.state === 'conductor'),
      getPosition: position,
      getIcon: () => ICON.disc,
      getSize: PLACE_ICON.discSize,
      sizeUnits: 'pixels',
      pickable: true,
      onClick: pick,
    }),
    new deck.IconLayer({
      id: 'place-icons',
      data: icons,
      getPosition: position,
      getIcon: (p: MapPlace) => (p.state === 'conductor' ? ICON.conductor : ICON.picaluna),
      getSize: (p: MapPlace) =>
        p.state === 'conductor' ? PLACE_ICON.conductorSize : PLACE_ICON.picalunaSize,
      sizeUnits: 'pixels',
      pickable: true,
      onClick: pick,
    }),
    new deck.IconLayer({
      id: 'place-badge-discs',
      data: both,
      getPosition: position,
      getIcon: () => ICON.disc,
      getSize: PLACE_ICON.badgeDiscSize,
      sizeUnits: 'pixels',
      getPixelOffset: PLACE_ICON.badgeOffset,
      pickable: false,
    }),
    new deck.IconLayer({
      id: 'place-badges',
      data: both,
      getPosition: position,
      getIcon: () => ICON.conductor,
      getSize: PLACE_ICON.badgeSize,
      sizeUnits: 'pixels',
      getPixelOffset: PLACE_ICON.badgeOffset,
      pickable: false,
    }),
  ];
}

function placeLabels(deck: any, places: MapPlace[]): any {
  return new deck.TextLayer({
    id: 'place-labels',
    data: places,
    getPosition: position,
    getText: (p: MapPlace) => p.name,
    getSize: PLACE_LABEL.size,
    sizeUnits: 'pixels',
    getColor: PLACE_LABEL.colour,
    outlineWidth: 4,
    outlineColor: PLACE_LABEL.outline,
    fontSettings: LABEL_FONT_SETTINGS,
    fontFamily: LABEL_FONT,
    fontWeight: 600,
    characterSet: 'auto',
    getTextAnchor: 'start',
    getAlignmentBaseline: 'center',
    getPixelOffset: (p: MapPlace) => [
      ICON_STATES.has(p.state) ? PLACE_LABEL.offset.icon : PLACE_LABEL.offset.dot,
      0,
    ],
    extensions: [new deck.CollisionFilterExtension()],
    collisionEnabled: true,
    collisionTestProps: { sizeScale: 1.4 },
    pickable: false,
  });
}

/** The pin being placed: an amber marker with a white ring. */
export function pinLayer(deck: any, pin: LngLat | null): any {
  if (!pin) return null;
  return new deck.ScatterplotLayer({
    id: 'new-pin',
    data: [pin],
    getPosition: (p: LngLat) => [p.lng, p.lat],
    getRadius: PIN.radius,
    radiusUnits: 'pixels',
    getFillColor: PIN.fill,
    getLineColor: PIN.line,
    getLineWidth: PIN.lineWidth,
    lineWidthUnits: 'pixels',
    stroked: true,
    pickable: false,
    updateTriggers: { getPosition: [pin.lng, pin.lat] },
  });
}
